import {
  Button,
  TextInput,
  Select,
  Textarea,
  UpIcon,
} from "@src/components/elements";
import { SelectOption } from "@src/components/elements/Select/SelectOption";
import { paymentMethodToFriendlyName } from "@src/utils/labels";
import React, { useEffect, useState } from "react";
import { Controller, useForm, WatchObserver } from "react-hook-form";
import styled from "styled-components/macro";
import { VendorFormValues } from "./VendorFormValues";
import { Chip } from "@src/components/elements/Chip";
import { FloatingSidebar } from "@src/components/elements/FloatingSidebar";
import {
  ErpVendor,
  ErpVendorToVendorEditor,
} from "@src/pages/vendor/VendorDetails/ErpVendorToVendorEditor";
import { RadioGroup } from "@src/components/elements/RadioGroup";
import {
  GetUpdateVendorDataQuery,
  PaymentMethod,
} from "@src/__generated__/graphql";
import { AsyncMemberSelect } from "@src/components/modules/AsyncSelect/AsyncMemberSelect";
import { AsyncMembersSelect } from "@src/components/modules/AsyncSelect/AsyncMembersSelect";
import { CustomVendorForm } from "@src/components/modules/VendorForm/CustomVendorForm";
import { QuestionIdToQuestionConfigurationMap } from "@src/pages/intake-requests/new/useNewIntakeRequest";
import { shouldShowField } from "@src/pages/intake-requests/new/useIntakeForm";
import _ from "lodash";
import { AddTaxPayerIdModal } from "./AddTaxPayerIdModal";

const StyledForm = styled.form`
  display: grid;
  grid-template-areas:
    "item1 ."
    "item2 item2"
    "item3 item4"
    "item5 item6"
    "item7 item8"
    "item9 item9"
    "custom ."
    "buttons buttons";
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto;
  column-gap: 1rem;
  row-gap: 1.5rem;

  .item1 {
    grid-area: item1;
  }
  .item2 {
    grid-area: item2;
  }
  .item3 {
    grid-area: item3;
  }
  .item4 {
    grid-area: item4;
  }
  .item5 {
    grid-area: item5;
  }
  .item6 {
    grid-area: item6;
  }
  .item7 {
    grid-area: item7;
  }
  .item8 {
    grid-area: item8;
  }
  .item9 {
    grid-area: item9;

    .radiogroup-title {
      font-weight: var(--font-weight-regular);
    }
  }

  .custom {
    grid-area: custom;
  }

  .label {
    font-family: var(--inter-font);
    font-size: var(--font-size-small);
  }

  .action-arrow {
    transform: rotate(90deg);
  }

  .buttons {
    grid-area: buttons;
    justify-self: start;
    align-self: start;
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  @media screen and (max-width: 40rem) {
    display: flex;
    flex-flow: column;
  }
`;

export const paymentMethodSelectOptions: SelectOption[] = Object.values(
  PaymentMethod,
).map((val) => ({
  label: paymentMethodToFriendlyName[val],
  value: val,
}));

interface VendorFormProps {
  onSubmit: (data: VendorFormValues) => void;
  onCancel: () => void;
  onSubmitAndAddService?: (data: VendorFormValues) => void;
  className?: string;
  erpVendorsWithoutMappings?: ErpVendor[];
  loading?: boolean;
  defaultValues: VendorFormValues;
  title: string;
  watch?: WatchObserver<VendorFormValues>;
  erpVendors: GetUpdateVendorDataQuery["erpVendorsByVendorId"];
  customQuestionIdToConfigurationMap: QuestionIdToQuestionConfigurationMap;
  shouldSoftValidateCustomFields?: boolean;
}

export const VendorForm: React.FC<VendorFormProps> = ({
  className,
  onSubmit,
  onCancel,
  onSubmitAndAddService,
  erpVendorsWithoutMappings,
  loading,
  defaultValues,
  title,
  watch,
  erpVendors,
  customQuestionIdToConfigurationMap,
  shouldSoftValidateCustomFields = false,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch: reactHookFormWatch,
    setValue,
    getValues,
    register,
  } = useForm<VendorFormValues>({
    defaultValues,
  });

  const [showErpVendorEditor, setShowErpVendorEditor] = useState(false);
  const [showAddTaxPayerIdModal, setShowAddTaxPayerIdModal] = useState(false);

  useEffect(() => {
    if (watch) {
      reactHookFormWatch((data, { name, type }) => {
        watch(data, { name, type });
      });
    }
  }, [reactHookFormWatch, watch]);

  const customQuestionIdToValueMap = reactHookFormWatch("customForm").reduce<{
    [
      key: string
    ]: VendorFormValues["customForm"][number]["fields"][number]["value"];
  }>((map, step) => {
    step.fields.forEach((field) => {
      map[field.questionId] = field.value;
    });

    return map;
  }, {});

  const mappedErpVendors = (getValues("erpVendors") || []).map<ErpVendor>(
    (option) => ({
      id: option.value,
      name: option.label,
      last12Spend: (
        erpVendors.find((ev) => ev.id === option.value) || { last12Spend: 0 }
      ).last12Spend,
    }),
  );

  const unmappedErpVendors = (erpVendorsWithoutMappings || []).filter(
    (erpVendor) =>
      mappedErpVendors.every((mapped) => mapped.id !== erpVendor.id),
  );

  return (
    <div className={className}>
      <h3 className="page-title">{title}</h3>
      <StyledForm>
        <div className="item1">
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => {
              return (
                <TextInput
                  isRequired={true}
                  type="text"
                  error={errors.name && "Vendor name is required"}
                  className={errors.name ? "error" : ""}
                  label="Vendor name"
                  placeholder="Vendor name"
                  {...field}
                />
              );
            }}
          />
        </div>

        <div className="item2">
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Textarea
                label="Vendor description"
                placeholder="Vendor description"
                className="description"
                {...field}
              />
            )}
          />
        </div>

        <div className="item3">
          <Controller
            name="owners"
            control={control}
            rules={{
              required: true,
              validate: {
                positive: (value) => value.length > 0,
              },
            }}
            render={({ field }) => {
              return (
                <AsyncMembersSelect
                  required
                  className="spec-vendor-owner"
                  label="Vendor owner"
                  placeholder="Vendor owner"
                  error={errors.owners && "Owner is required"}
                  value={field.value.map((v) => v.value)}
                  onChange={(values) => {
                    field.onChange(values);
                  }}
                />
              );
            }}
          />
        </div>

        <div className="item4">
          <Controller
            name="signer"
            control={control}
            render={({ field }) => {
              const { ref, ...rest } = field;
              return (
                <AsyncMemberSelect
                  className="spec-vendor-signer"
                  label="Vendor signer"
                  placeholder="Vendor signer"
                  isSigner={true}
                  {...rest}
                  value={field.value ?? null}
                  clearable={true}
                  onChange={(value) => {
                    if (value?.value === "") {
                      field.onChange(undefined);
                    } else {
                      field.onChange(value);
                    }
                  }}
                />
              );
            }}
          />
        </div>

        <div className="item5">
          <Controller
            name="externalPointOfContact"
            control={control}
            render={({ field }) => (
              <TextInput
                label="External point of contact"
                placeholder="External point of contact"
                {...field}
              />
            )}
          />
        </div>

        <div className="item6">
          <Controller
            name="externalSigner"
            control={control}
            render={({ field }) => (
              <TextInput
                label="External signer"
                placeholder="External signer"
                {...field}
              />
            )}
          />
        </div>

        <div className="item7">
          <Controller
            name="paymentMethod"
            control={control}
            rules={{
              required: true,
              validate: {
                positive: (value) => value.value !== "",
              },
            }}
            render={({ field }) => (
              <Select
                required
                className="spec-vendor-payment-method"
                placeholder="Payment method"
                label="Payment method"
                options={paymentMethodSelectOptions || []}
                error={errors.paymentMethod && "Payment method is required"}
                {...field}
              />
            )}
          />
        </div>

        <div className="item8">
          <Controller
            name="taxPayerIdentificationNumbers"
            control={control}
            render={({ field }) => (
              <div>
                <label className="label">EIN/TIN(s)</label>
                <div className="column start">
                  {field.value.map((tin, index) => (
                    <Chip
                      key={`tin-${index}`}
                      onRemove={() => {
                        const newTins = [...field.value];
                        newTins.splice(index, 1);
                        field.onChange(newTins);
                      }}
                    >
                      {tin.taxPayerIdentificationNumber}
                    </Chip>
                  ))}
                  <Button
                    variant="transparent"
                    size="small"
                    onClick={() => setShowAddTaxPayerIdModal(true)}
                  >
                    Add EIN/TIN
                  </Button>
                </div>
              </div>
            )}
          />
        </div>

        <div className="item9">
          <Controller
            control={control}
            name="isSocTwoCompliant"
            render={({ field }) => {
              return (
                <div>
                  <RadioGroup
                    isRequired={true}
                    label="Is this vendor SOC 2 Type II compliant?"
                    onChange={(valueString) => {
                      const value = JSON.parse(valueString);
                      field.onChange(value);
                    }}
                    options={[
                      { label: "Yes", value: "true" },
                      { label: "No", value: "false" },
                      { label: "Not applicable", value: "null" },
                    ]}
                    selectedItem={JSON.stringify(field.value)}
                  />
                </div>
              );
            }}
          />
        </div>

        <div className="item10">
          <h4 className="label">ERP Vendor(s)</h4>
          <Controller
            name="erpVendors"
            control={control}
            rules={{
              required: false,
            }}
            render={({ field }) => (
              <div className="column start">
                {field.value.map((erpVendor) => (
                  <Chip
                    id={erpVendor.value}
                    onRemove={(id) => {
                      setValue(
                        "erpVendors",
                        field.value.filter((v) => v.value !== id),
                      );
                    }}
                    key={erpVendor.value}
                  >
                    {erpVendor.label}
                  </Chip>
                ))}
                <Button
                  variant="transparent"
                  size="small"
                  onClick={() => {
                    setShowErpVendorEditor(true);
                  }}
                >
                  <span>Edit ERP Vendors</span>
                  <UpIcon className="action-arrow" />
                </Button>
              </div>
            )}
          />
        </div>

        <CustomVendorForm
          className="custom"
          formType="VENDOR"
          customQuestionIdToValueMap={customQuestionIdToValueMap}
          customQuestionIdToConfigurationMap={
            customQuestionIdToConfigurationMap
          }
          shouldSoftValidate={shouldSoftValidateCustomFields}
          setValue={setValue}
          customForm={reactHookFormWatch("customForm")}
          register={register}
        />

        <div className="buttons">
          <Button onClick={onCancel} variant="tertiary">
            Cancel
          </Button>
          {onSubmitAndAddService && (
            <Button
              variant="secondary"
              onClick={async () => {
                const submitter = handleSubmit((data, event) => {
                  const copy = _.cloneDeep(data);
                  copy.customForm = copy.customForm.map((step) => ({
                    ...step,
                    fields: step.fields.filter((field) => {
                      const show = shouldShowField(
                        field.questionId,
                        customQuestionIdToConfigurationMap,
                        customQuestionIdToValueMap,
                      );
                      return show;
                    }),
                  }));
                  event?.preventDefault();
                  onSubmitAndAddService(copy);
                });
                void submitter();
              }}
              loading={loading}
            >
              Save and Add Service
            </Button>
          )}
          <Button
            onClick={async () => {
              const submitter = await handleSubmit((data, event) => {
                const copy = _.cloneDeep(data);
                copy.customForm = copy.customForm.map((step) => ({
                  ...step,
                  fields: step.fields.filter((field) => {
                    const show = shouldShowField(
                      field.questionId,
                      customQuestionIdToConfigurationMap,
                      customQuestionIdToValueMap,
                    );
                    return show;
                  }),
                }));

                event?.preventDefault();
                onSubmit(copy);
              });
              void submitter();
            }}
            loading={loading}
          >
            Save
          </Button>
        </div>
      </StyledForm>
      {showErpVendorEditor && (
        <FloatingSidebar
          title="Add ERP vendor"
          onClose={() => setShowErpVendorEditor(false)}
        >
          <ErpVendorToVendorEditor
            mappedErpVendors={mappedErpVendors}
            erpVendorsWithoutMappings={unmappedErpVendors}
            onCancel={() => setShowErpVendorEditor(false)}
            onSave={async (_, erpVendorsToAdd: ErpVendor[]) => {
              setShowErpVendorEditor(false);
              setValue("erpVendors", [
                ...erpVendorsToAdd.map((erp) => ({
                  label: erp.name,
                  value: erp.id,
                })),
              ]);
            }}
            loading={false}
          />
        </FloatingSidebar>
      )}
      {showAddTaxPayerIdModal && (
        <AddTaxPayerIdModal
          onClose={() => setShowAddTaxPayerIdModal(false)}
          onAdd={(taxPayerId) => {
            const currentTins =
              getValues("taxPayerIdentificationNumbers") || [];
            setValue("taxPayerIdentificationNumbers", [
              ...currentTins,
              taxPayerId,
            ]);
          }}
        />
      )}
    </div>
  );
};
